import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary } from './CommissionOverview';
import { FormRow, overlayStyle, modalStyle } from './CommissionBeneficiaries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ADJUSTMENT_TYPES = ['override', 'bonus', 'increase', 'deduction', 'penalty', 'correction', 'carry_forward', 'negative_balance'];
const STATUSES = ['pending_approval', 'applied', 'rejected'];

export default function CommissionAdjustments() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('');
  const [entries, setEntries] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/adjustments?per_page=50`;
      if (status) url += `&status=${status}`;
      if (adjustmentType) url += `&adjustment_type=${adjustmentType}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, adjustmentType]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/commissions/entries?per_page=200`, { headers: getAuthHeaders() })
      .then(res => res.ok ? res.json() : { data: [] }).then(j => setEntries(Array.isArray(j.data) ? j.data : [])).catch(() => setEntries([]));
  }, [companyId]);

  const approve = async (id) => {
    try {
      await fetch(`${API}/companies/${companyId}/commissions/adjustments/${id}/approve`, { method: 'POST', headers: getAuthHeaders() });
      fetchList();
    } catch (e) { console.error(e); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      await fetch(`${API}/companies/${companyId}/commissions/adjustments/${id}/reject`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
      });
      fetchList();
    } catch (e) { console.error(e); }
  };

  const fmt = (v) => v === null || v === undefined ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 });

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚖️ Commission Adjustments</h2>
        <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ New Adjustment</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select value={adjustmentType} onChange={e => setAdjustmentType(e.target.value)} style={{ ...inputStyle, width: 180 }}>
          <option value="">All Types</option>
          {ADJUSTMENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 160 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['#', 'Entry', 'Beneficiary', 'Type', 'Original', 'Value', 'Final', 'Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No adjustments yet</td></tr>}
              {list.map(a => (
                <tr key={a.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{a.adjustment_number}</td>
                  <td style={tdStyle}>{a.entry?.entry_number || '—'}</td>
                  <td style={tdStyle}>{a.beneficiary?.display_name || '—'}</td>
                  <td style={tdStyle}>{(a.adjustment_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{fmt(a.original_value)}</td>
                  <td style={tdStyle}>{fmt(a.adjustment_value)}</td>
                  <td style={tdStyle}>{fmt(a.final_value)}</td>
                  <td style={tdStyle}><Badge value={a.status} /></td>
                  <td style={tdStyle}>
                    {a.status === 'pending_approval' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={btnSecondary} onClick={() => approve(a.id)}>Approve</button>
                        <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => reject(a.id)}>Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateAdjustmentModal companyId={companyId} entries={entries} onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchList(); }} />
      )}
    </div>
  );
}

function CreateAdjustmentModal({ companyId, entries, onClose, onCreated }) {
  const [form, setForm] = useState({ entry_id: '', beneficiary_id: '', adjustment_type: 'bonus', override_field: '', adjustment_value: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!form.adjustment_type) { setErr('Adjustment type is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const res = await fetch(`${API}/companies/${companyId}/commissions/adjustments`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Failed to create'); }
      onCreated();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Commission Adjustment</h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <FormRow label="Entry">
          <select value={form.entry_id} onChange={e => setForm({ ...form, entry_id: e.target.value })} style={inputStyle}>
            <option value="">None (beneficiary-level)</option>
            {entries.map(en => <option key={en.id} value={en.id}>{en.entry_number}</option>)}
          </select>
        </FormRow>
        <FormRow label="Adjustment Type *">
          <select value={form.adjustment_type} onChange={e => setForm({ ...form, adjustment_type: e.target.value })} style={inputStyle}>
            {ADJUSTMENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </FormRow>
        {form.adjustment_type === 'override' && (
          <FormRow label="Override Field"><input value={form.override_field} onChange={e => setForm({ ...form, override_field: e.target.value })} style={inputStyle} placeholder="e.g. final_amount" /></FormRow>
        )}
        <FormRow label="Adjustment Value"><input type="number" value={form.adjustment_value} onChange={e => setForm({ ...form, adjustment_value: e.target.value })} style={inputStyle} /></FormRow>
        <FormRow label="Reason"><textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} /></FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
